import { actions, actionTypes, selectors } from 'data'
import { AuthedActionType, BalanceStopType, SetUserSettingsType } from 'data/actions'
import { BalanceMessageType, SubscriptionMessageType } from 'data/types'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import webSocket from 'services/api/webSocket'
import { logger, sanitizeMessage } from 'utils'

import { BalanceRejectedMessageType, BalanceSnapshotMessageType, RawBalanceType } from './types'

const messageManager = function* (action) {
  const message: BalanceMessageType = action.payload

  if (message.event === webSocket.event.REJECTED) {
    logger.warn((message as BalanceRejectedMessageType).text)
    return
  }

  if (message.event === webSocket.event.SNAPSHOT) {
    performance.mark('balance-snapshot')
    performance.measure('Balances', 'balance-subscribe', 'balance-snapshot')
    const snapshotMessage = message as BalanceSnapshotMessageType

    yield put(
      actions.balance.setSnapshot(
        snapshotMessage.balances,
        snapshotMessage.total_available_local,
        snapshotMessage.total_balance_local
      )
    )
    return
  }

  if (message.event === webSocket.event.UPDATED) {
    yield put(actions.balance.setUpdate(sanitizeMessage(message) as RawBalanceType))
    return
  }

  if (message.event === webSocket.event.SUBSCRIBED) {
    yield put(actions.balance.setSubscribed())
  }
}

const start = function* () {
  const localCcy: string = yield select(selectors.auth.getLocalCurrency)
  const channel = webSocket.channel.BALANCES
  const subscriptionMessage: SubscriptionMessageType = {
    batching: true,
    channel,
    local_currency: localCcy,
    refresh: true
  }
  performance.mark('balance-subscribe')
  yield put(actions.socket.subscribe(subscriptionMessage))
}

const init = function* (action: AuthedActionType) {
  if (action.payload.authenticated) {
    yield call(start)
  } else {
    yield put(actions.balance.reset())
  }
}

const changeCurrency = function* (action: SetUserSettingsType) {
  if (action.payload.prevSettings.localCurrency !== action.payload.settings.localCurrency) {
    if (action.payload.prevSettings.localCurrency) {
      yield put(actions.balance.stop(action.payload.prevSettings.localCurrency))
    }
    yield put(actions.balance.reset())
    yield call(start)
  }
}

const stop = function* (action: BalanceStopType) {
  yield put(
    actions.socket.unsubscribe({
      batching: true,
      channel: webSocket.channel.BALANCES,
      local_currency: action.payload.currency
    })
  )
}

export default function* balanceMain() {
  yield takeEvery(actionTypes.auth.SET_SOCKET_AUTHED, init)
  yield takeEvery(actionTypes.balance.STOP, stop)
  yield takeEvery(actionTypes.auth.SET_USER_SETTINGS, changeCurrency)
  const channel = webSocket.channel.BALANCES
  yield takeEvery(
    (action) => action.type === actionTypes.socket.RECEIVED && action.payload.channel === channel,
    messageManager
  )
}
